import { RouteConfig } from '../interfaces/routes-config.interface';
import { UserRole } from '../enums/user-role.enum';
// tslint:disable: no-console
console.group();
console.info('*** PUBLIC PATH *** ', process.env.PUBLIC_URL);
console.info('*** BE ROUTE ***', process.env.REACT_APP_BE_API_ENDPOINT);
console.groupEnd();
// tslint:enable: no-console
export const PUBLIC_PATH = process.env.PUBLIC_URL || '/';
export const SERVER_BASE_URL = process.env.REACT_APP_BE_API_ENDPOINT;
export const HOST_NAME = '//' + window.location.host;
export const WEBSITE_HOME = process.env.REACT_APP_WEBSITE_BASE || 'https://birdnestlife.com';
export const GOOGLE_CAPTCHA_KEY = process.env.REACT_APP_GOOGLE_CAPTCHA_KEY;

export const GES_PERMITTED_ROLES: UserRole[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.GES,
  UserRole.OPERATION_MANAGER
];

export const CX_QUEUE_PERMITTED_ROLES: UserRole[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.RESERVATIONS,
  UserRole.RESERVATION_MANAGER,
  UserRole.OPERATION_MANAGER
];
/**
 * Tickets (#244): every back-office role. Mirrors the authenticator's seed of
 * ticketsRead/Create/ModifyAccess, which gives them to all staff roles except
 * guest, landlord, investor, partnerManager and channelIntegrator. The server
 * gate is the permission, not this list; this only keeps the page from
 * showing to people the API will refuse.
 */
export const TICKETS_PERMITTED_ROLES: UserRole[] = Object.values(UserRole).filter(
  (role: UserRole) =>
    ![
      UserRole.VISITOR,
      UserRole.ONLINE_USER,
      UserRole.GUEST,
      UserRole.LANDLORD,
      UserRole.INVESTOR,
      UserRole.PARTNER_MANAGER
    ].includes(role)
);

export const PROPERTY_MANAGERS_PERMITTED_ROLES: UserRole[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.OPERATION_MANAGER,
  UserRole.HOSPITALITY_MANAGER
];
export const CALENDAR_PERMITTED_ROLES: UserRole[] = [
  UserRole.SUPER_ADMIN,
  UserRole.E_COMMERCE,
  UserRole.ECOMMERCE_SPECIALIST,
  UserRole.ECOMMERCE_SENIOR_SPECIALIST,
  UserRole.SALES_SPECIALIST,
  UserRole.SALES_MANAGER,
  UserRole.ADMIN,
  UserRole.FINANCE_MANAGER,
  UserRole.FINANCE_SPECIALIST,
  UserRole.RESERVATION_MANAGER,
  UserRole.RESERVATION_AGENT,
  UserRole.RESERVATIONS,
  UserRole.HOSPITALITY_AGENT,
  UserRole.HOSPITALITY_LEAD,
  UserRole.GES,
  UserRole.HOSPITALITY_MANAGER,
  UserRole.HOUSE_KEEPING_MANAGER,
  UserRole.OPERATION_MANAGER,
  UserRole.COMMERCIAL_MANAGER
];
export const FINANCE_PERMITTED_ROLES: UserRole[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.FINANCE_MANAGER,
  UserRole.FINANCE_SPECIALIST
];

/**
 * Who the dashboard is for. Lifted out of the inline list on the DashboardModule route in
 * index.tsx so the /home landing redirect can ask the same question the route guard asks —
 * if these two disagree, someone gets redirected to a page that then refuses them.
 */
export const DASHBOARD_PERMITTED_ROLES: UserRole[] = [
  UserRole.E_COMMERCE,
  UserRole.ECOMMERCE_SPECIALIST,
  UserRole.ECOMMERCE_SENIOR_SPECIALIST,
  UserRole.SALES_SPECIALIST,
  UserRole.SALES_MANAGER,
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN,
  UserRole.FINANCE_MANAGER,
  UserRole.FINANCE_SPECIALIST,
  UserRole.RESERVATION_MANAGER,
  UserRole.RESERVATION_AGENT,
  UserRole.RESERVATIONS,
  UserRole.HOSPITALITY_AGENT,
  UserRole.HOSPITALITY_LEAD,
  UserRole.GES,
  UserRole.HOSPITALITY_MANAGER,
  UserRole.HOUSE_KEEPING_MANAGER,
  // UserRole.HOUSE_KEEPING,
  UserRole.OPERATION_MANAGER,
  UserRole.COMMERCIAL_MANAGER,
  UserRole.INVESTOR
];

/**
 * Who the KPIs page is for. Lifted out of the inline list on the KpisModule route in index.tsx
 * so the side nav asks the same question the route guard asks — KPIS_MODULE carried no
 * PERMITTED_ROLES at all, and side-menu.component.tsx reads a missing list as "show to
 * everyone", so every role saw a KPIs entry that then bounced them off the page.
 */
/**
 * Who User Management is for. Lifted out of the two inline [UserRole.SUPER_ADMIN] arrays that
 * used to sit on the route config and on the DefaultLayout in index.tsx, so the side nav and
 * the route guard cannot drift — the same reason KPIS_PERMITTED_ROLES exists.
 *
 * admin joins superAdmin here so an admin can create staff users. Note this gates the screen
 * only: what the endpoint itself allows is decided by the caller's usersCreateAccess /
 * usersFullAccess permission in the authenticator's permissions collection.
 */
export const USER_MANAGEMENT_PERMITTED_ROLES: UserRole[] = [UserRole.SUPER_ADMIN, UserRole.ADMIN];

export const KPIS_PERMITTED_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN,
  UserRole.ECOMMERCE_SENIOR_SPECIALIST,
  UserRole.OPERATION_MANAGER,
  // UserRole.RESERVATION_MANAGER,
  UserRole.HOSPITALITY_MANAGER,
  UserRole.FINANCE_MANAGER,
  UserRole.INVESTOR,
  UserRole.COMMERCIAL_MANAGER
];

/**
 * The marketing roles proper — the ones whose whole job is the Marketing tab.
 *
 * Neither is on the dashboard's permitted list, so sending them to /home the way everyone else
 * lands would drop them on Unauthorized. They go to Blogs instead: the one module both of them
 * can always reach. See the /home redirect in index.tsx.
 */
export const MARKETING_ONLY_ROLES: UserRole[] = [
  UserRole.MARKETING_SPECIALIST,
  UserRole.MARKETING_CAMPAIGNS
];

/**
 * Who reaches the Blogs module.
 *
 * Both marketing roles: writing blogs is the shared part of the job.
 */
export const MARKETING_BLOGS_PERMITTED_ROLES: UserRole[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  ...MARKETING_ONLY_ROLES
];

/**
 * Who reaches the guest audience report.
 *
 * Campaigns but not specialist — the report pairs guest contact details with what each guest has
 * paid, which is more than the Blogs job needs. Hiding the menu entry is all this list does; the
 * gate that matters is the matching role list in admin-connect's report.route.ts, and the two
 * have to be changed together.
 */
export const MARKETING_GUEST_AUDIENCE_PERMITTED_ROLES: UserRole[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.MARKETING_CAMPAIGNS
];

/**
 * Who sees the Marketing tab at all — anyone who can reach something inside it. The children
 * carry their own lists, so a role only sees the entries it is allowed.
 */
export const MARKETING_PERMITTED_ROLES: UserRole[] = Array.from(
  new Set([...MARKETING_BLOGS_PERMITTED_ROLES, ...MARKETING_GUEST_AUDIENCE_PERMITTED_ROLES])
);

/**
 * Who reaches the Payments module and everything under it.
 *
 * One list rather than a copy per entry: the module and its two pages have always had the same
 * audience, and three copies of it is how they drift apart. The gate that matters for the overdue
 * payments report is the permission check in the property manager's overdue-payments.route.ts —
 * this only decides who sees the menu entry.
 */
export const PAYMENTS_PERMITTED_ROLES: UserRole[] = [
  UserRole.FINANCE_MANAGER,
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN,
  UserRole.FINANCE_SPECIALIST,
  UserRole.RESERVATION_MANAGER,
  UserRole.RESERVATIONS,
  UserRole.OPERATION_MANAGER
];

export const SALES_PERSONAL_PERMITTED_ROLES: UserRole[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.RESERVATIONS,
  UserRole.RESERVATION_MANAGER
];

export const PROMOCODES_PERMITTED_ROLES: UserRole[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.ECOMMERCE_SENIOR_SPECIALIST,
  UserRole.ECOMMERCE_SPECIALIST
];

export const APP_ROUTES: RouteConfig = {
  HOME_MODULE: {
    PATH: '/home',
    FULL_PATH: '/home',
    DISPLAY_NAME: 'Dashboard',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    NAME: 'home',
    ICON: `${PUBLIC_PATH}/assets/pngs/side-nav/home.png`
  },
  CALENDAR_MODULE: {
    PATH: '/calendar',
    FULL_PATH: '/calendar',
    DISPLAY_NAME: 'Calendar',
    NAME: 'Calendar',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/dashboard.svg`,
    PERMITTED_ROLES: CALENDAR_PERMITTED_ROLES
  },
  DASHBOARD_MODULE: {
    PATH: '/dashboard',
    FULL_PATH: '/dashboard',
    DISPLAY_NAME: 'Dashboard',
    NAME: 'dashboard',
    IS_MENU: false,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/dashboard.svg`,
    CHILDREN: {
      HOME: {
        PATH: '/home',
        FULL_PATH: '/dashboard/home',
        NAME: 'Home',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false
      },
      OVERVIEW: {
        PATH: '/overview',
        FULL_PATH: '/dashboard/overview',
        NAME: 'Overview',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false
      }
    }
  },

  KPIS_MODULE: {
    PATH: '/kpis',
    FULL_PATH: '/kpis',
    DISPLAY_NAME: 'Kpis',
    NAME: 'Kpis',
    IS_MENU: false,
    IS_MENU_LOOKUP: true,
    IS_MENU_USERS: false,
    PERMITTED_ROLES: KPIS_PERMITTED_ROLES,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/kpis.svg`,
    CHILDREN: {
      HOME: {
        PATH: '/home',
        FULL_PATH: '/kpis/home',
        NAME: 'Kpis',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false
      }
      // BUSINESS_ON_THE_BOOK: {
      //   PATH: '/BusinessOnTheBook',
      //   FULL_PATH: '/kpis/BusinessOnTheBook',
      //   DISPLAY_NAME: 'Business on the book',
      //   NAME: 'Business on the book',
      //   ICON: `${PUBLIC_PATH}/assets/pngs/side-nav/area.png`,
      //   IS_MENU: false,
      //   IS_MENU_LOOKUP: true,
      //   IS_MENU_USERS: false
      // }
    }
  },
  OCCUPANCY_RATE_MODULE: {
    PATH: '/occupancy-rate',
    FULL_PATH: '/occupancy-rate',
    DISPLAY_NAME: 'Occupancy rate',
    NAME: 'Occupancy rate',
    IS_MENU: false,
    IS_MENU_LOOKUP: true,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/occupancy-rate.svg`,
    // The dashboard carries revenue, ADR and block cost, so this list is narrower than the old
    // occupancy page's. Set by product on 2026-09-28; it must agree with OCCUPANCY_DASHBOARD_ROLES
    // in server.service.property, which is the gate that holds. This one only hides the menu entry
    // and the route.
    PERMITTED_ROLES: [
      UserRole.SUPER_ADMIN,
      UserRole.ADMIN,
      UserRole.COMMERCIAL_MANAGER,
      UserRole.FINANCE_MANAGER,
      UserRole.SALES_MANAGER,
      UserRole.OPERATION_MANAGER,
      UserRole.ECOMMERCE_SENIOR_SPECIALIST
    ]
  },
  DAILY_AVAILABILITY_MODULE: {
    PATH: '/daily-availability',
    FULL_PATH: '/daily-availability',
    DISPLAY_NAME: 'Daily Occupancy rate',
    NAME: 'Daily Occupancy rate',
    IS_MENU: false,
    IS_MENU_LOOKUP: true,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/occupancy-rate.svg`,
    PERMITTED_ROLES: [
      UserRole.SUPER_ADMIN,
      UserRole.ADMIN,
      UserRole.SALES_SPECIALIST,
      UserRole.ECOMMERCE_SENIOR_SPECIALIST,
      UserRole.ECOMMERCE_SPECIALIST,
      UserRole.SALES_MANAGER,
      UserRole.OPERATION_MANAGER
    ]
  },
  WEBSITE_SORT_MODULE: {
    PATH: '/website-sort',
    FULL_PATH: '/website-sort',
    DISPLAY_NAME: 'Website Sort',
    NAME: 'Website Sort',
    IS_MENU: false,
    IS_MENU_LOOKUP: true,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/occupancy-rate.svg`,
    PERMITTED_ROLES: [UserRole.SUPER_ADMIN]
  },
  AUTH_MODULE: {
    PATH: '/auth',
    FULL_PATH: '/auth',
    NAME: 'auth',
    IS_MENU: false,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    CHILDREN: {
      LOGIN: {
        PATH: '/login',
        FULL_PATH: '/auth/login',
        NAME: 'Sign in',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false
      },
      RESET_PASSWORD: {
        PATH: '/reset-password',
        FULL_PATH: '/auth/reset-password',
        NAME: 'Reset password',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false
      },
      FORGOT_PASSWORD: {
        PATH: '/forgot-password',
        FULL_PATH: '/auth/forgot-password',
        NAME: 'Forgot password',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false
      },
      UNAUTHORIZED: {
        PATH: '/unauthorized',
        FULL_PATH: '/auth/unauthorzied',
        NAME: 'Unauthorized',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false
      }
    }
  },
  AREA_MODULE: {
    PATH: '/areas',
    FULL_PATH: '/areas',
    DISPLAY_NAME: 'Areas',
    NAME: 'areas',
    IS_MENU: false,
    IS_MENU_LOOKUP: true,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/location.svg`,
    PERMITTED_ROLES: [
      UserRole.E_COMMERCE,
      UserRole.ECOMMERCE_SPECIALIST,
      UserRole.OPERATION_MANAGER,
      UserRole.ECOMMERCE_SENIOR_SPECIALIST,
      UserRole.ADMIN,
      UserRole.SUPER_ADMIN
    ],
    CHILDREN: {
      LIST: {
        PATH: '/list',
        FULL_PATH: '/areas/list',
        NAME: 'Areas List',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: [
          UserRole.E_COMMERCE,
          UserRole.ECOMMERCE_SPECIALIST,
          UserRole.OPERATION_MANAGER,
          UserRole.ECOMMERCE_SENIOR_SPECIALIST,
          UserRole.ADMIN,
          UserRole.SUPER_ADMIN
        ]
      }
    }
  },
  UNIT_TYPE_GROUP_MODULE: {
    PATH: '/unitTypeGroups',
    FULL_PATH: '/unitTypeGroups',
    DISPLAY_NAME: 'Accommodation Types',
    NAME: 'unitTypeGroups',
    IS_MENU: false,
    IS_MENU_LOOKUP: true,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/accommodation.svg`,
    PERMITTED_ROLES: [
      UserRole.E_COMMERCE,
      UserRole.ECOMMERCE_SPECIALIST,
      UserRole.ECOMMERCE_SENIOR_SPECIALIST,
      UserRole.ADMIN,
      UserRole.SUPER_ADMIN
    ],
    CHILDREN: {
      LIST: {
        PATH: '/list',
        FULL_PATH: '/unitTypeGroups/list',
        NAME: 'Accommodation Type List',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: [
          UserRole.E_COMMERCE,
          UserRole.ECOMMERCE_SPECIALIST,
          UserRole.ECOMMERCE_SENIOR_SPECIALIST,
          UserRole.ADMIN,
          UserRole.SUPER_ADMIN
        ]
      }
    }
  },
  AMENITY_MODULE: {
    PATH: '/amenity',
    FULL_PATH: '/amenity',
    DISPLAY_NAME: 'Amenities',
    NAME: 'amenity',
    IS_MENU: false,
    IS_MENU_LOOKUP: true,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/amenities.svg`,
    PERMITTED_ROLES: [
      UserRole.E_COMMERCE,
      UserRole.ECOMMERCE_SPECIALIST,
      UserRole.ECOMMERCE_SENIOR_SPECIALIST,
      UserRole.ADMIN,
      UserRole.SUPER_ADMIN
    ],
    CHILDREN: {
      LIST: {
        PATH: '/list',
        FULL_PATH: '/amenity/list',
        NAME: 'Amenities List',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: [
          UserRole.E_COMMERCE,
          UserRole.ECOMMERCE_SPECIALIST,
          UserRole.ECOMMERCE_SENIOR_SPECIALIST,
          UserRole.ADMIN,
          UserRole.SUPER_ADMIN
        ]
      }
    }
  },
  USER_MODULE: {
    PATH: '/user',
    FULL_PATH: '/user',
    DISPLAY_NAME: 'User Search',
    NAME: 'user',
    IS_MENU: false,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: true,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/user-profile.svg`,
    PERMITTED_ROLES: [
      UserRole.E_COMMERCE,
      UserRole.ECOMMERCE_SPECIALIST,
      UserRole.ECOMMERCE_SENIOR_SPECIALIST,
      UserRole.SALES_SPECIALIST,
      UserRole.SALES_MANAGER,
      UserRole.ADMIN,
      UserRole.SUPER_ADMIN,
      UserRole.RESERVATIONS,
      UserRole.RESERVATION_MANAGER,
      UserRole.RESERVATION_AGENT,
      UserRole.HOSPITALITY_MANAGER,
      UserRole.HOSPITALITY_AGENT,
      UserRole.HOSPITALITY_LEAD,
      UserRole.GES,
      UserRole.OPERATION_MANAGER,
      UserRole.COMMERCIAL_MANAGER
    ],
    CHILDREN: {
      SEARCH: {
        PATH: '/search',
        FULL_PATH: '/user/search',
        NAME: 'User Search',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: true,
        PERMITTED_ROLES: [
          UserRole.E_COMMERCE,
          UserRole.ECOMMERCE_SPECIALIST,
          UserRole.ECOMMERCE_SENIOR_SPECIALIST,
          UserRole.SALES_SPECIALIST,
          UserRole.SALES_MANAGER,
          UserRole.ADMIN,
          UserRole.SUPER_ADMIN,
          UserRole.RESERVATIONS,
          UserRole.RESERVATION_MANAGER,
          UserRole.RESERVATION_AGENT,
          UserRole.HOSPITALITY_MANAGER,
          UserRole.HOSPITALITY_AGENT,
          UserRole.HOSPITALITY_LEAD,
          UserRole.GES,
          UserRole.OPERATION_MANAGER,
          UserRole.COMMERCIAL_MANAGER
        ]
      }
    }
  },
  GUEST_MANAGEMENT_MODULE: {
    PATH: '/guest-management',
    FULL_PATH: '/guest-management',
    DISPLAY_NAME: 'Guest Management',
    NAME: 'guest-management',
    IS_MENU: false,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: true,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/user-profile.svg`,
    PERMITTED_ROLES: [UserRole.SUPER_ADMIN],
    CHILDREN: {
      SEARCH: {
        PATH: '/search',
        FULL_PATH: '/guest-management/search',
        NAME: 'Guest Management Search',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: true,
        PERMITTED_ROLES: [UserRole.SUPER_ADMIN]
      }
    }
  },
  USER_MANAGEMENT_MODULE: {
    PATH: '/user-management',
    FULL_PATH: '/user-management',
    DISPLAY_NAME: 'User Management',
    NAME: 'user-management',
    IS_MENU: false,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: true,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/user-profile.svg`,
    // Collapsible so the three audiences sit under one nav entry. side-menu renders a
    // collapsible parent's IS_SUBMENU children; a child's own IS_MENU_USERS is never read.
    IS_COLLAPSIBLE: true,
    PERMITTED_ROLES: USER_MANAGEMENT_PERMITTED_ROLES,
    CHILDREN: {
      GUESTS: {
        PATH: '/guests',
        FULL_PATH: '/user-management/guests',
        NAME: 'Guests',
        DISPLAY_NAME: 'Guests',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: USER_MANAGEMENT_PERMITTED_ROLES
      },
      STAFF: {
        PATH: '/staff',
        FULL_PATH: '/user-management/staff',
        NAME: 'Staff Users',
        DISPLAY_NAME: 'Staff Users',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: USER_MANAGEMENT_PERMITTED_ROLES
      },
      STAFF_CREATE: {
        PATH: '/staff/create',
        FULL_PATH: '/user-management/staff/create',
        NAME: 'Create Staff User',
        DISPLAY_NAME: 'Create Staff User',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: USER_MANAGEMENT_PERMITTED_ROLES
      }
    }
  },
  LANDLORD_MODULE: {
    PATH: '/landlords',
    FULL_PATH: '/landlords',
    DISPLAY_NAME: 'Landlords',
    NAME: 'landlords',
    IS_MENU: false,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: true,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/user-profile.svg`,
    PERMITTED_ROLES: FINANCE_PERMITTED_ROLES,
    CHILDREN: {
      DETAILS: {
        PATH: '/:landlordId',
        FULL_PATH: '/landlords/:landlordId',
        NAME: 'Landlord Details',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: FINANCE_PERMITTED_ROLES
      }
    }
  },
  SALES_PERSONS_MODULE: {
    PATH: '/sales-persons',
    FULL_PATH: '/sales-persons',
    DISPLAY_NAME: 'Sales Persons',
    NAME: 'sales-persons',
    IS_MENU: false,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: true,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/user-profile.svg`,
    PERMITTED_ROLES: SALES_PERSONAL_PERMITTED_ROLES
  },
  BOOKING_MODULE: {
    PATH: '/booking',
    FULL_PATH: '/booking',
    DISPLAY_NAME: ' Booking Requests',
    NAME: ' Booking Requests',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    NOTIFICATIONS_KEY: 'bookingRequests',
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/amenities.svg`,
    PERMITTED_ROLES: [
      UserRole.RESERVATION_AGENT,
      UserRole.RESERVATION_MANAGER,
      UserRole.ADMIN,
      UserRole.SUPER_ADMIN,
      UserRole.COMMERCIAL_MANAGER,
      UserRole.RESERVATIONS
    ],
    CHILDREN: {
      LIST: {
        PATH: '/list',
        FULL_PATH: '/booking/list',
        NAME: ' Booking Requests List',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: [
          UserRole.RESERVATION_AGENT,
          UserRole.RESERVATION_MANAGER,
          UserRole.ADMIN,
          UserRole.SUPER_ADMIN,
          UserRole.COMMERCIAL_MANAGER,
          UserRole.RESERVATIONS
        ]
      }
    }
  },
  BLOCK_DATES_MODULE: {
    PATH: '/block-dates',
    FULL_PATH: '/block-dates',
    DISPLAY_NAME: 'Block Dates',
    NAME: 'Block Dates',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    NOTIFICATIONS_KEY: 'blockDates',
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/amenities.svg`,
    PERMITTED_ROLES: [
      UserRole.RESERVATION_AGENT,
      UserRole.RESERVATION_MANAGER,
      UserRole.ECOMMERCE_SPECIALIST,
      UserRole.ECOMMERCE_SENIOR_SPECIALIST,
      UserRole.RESERVATIONS,
      UserRole.OPERATION_MANAGER,
      UserRole.ADMIN,
      UserRole.SUPER_ADMIN
    ],
    CHILDREN: {
      LIST: {
        PATH: '/list',
        FULL_PATH: '/block-dates/list',
        NAME: 'Block Dates List',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: [
          UserRole.RESERVATION_AGENT,
          UserRole.RESERVATION_MANAGER,
          UserRole.ECOMMERCE_SPECIALIST,
          UserRole.ECOMMERCE_SENIOR_SPECIALIST,
          UserRole.RESERVATIONS,
          UserRole.OPERATION_MANAGER,
          UserRole.SUPER_ADMIN,
          UserRole.ADMIN
        ]
      }
    }
  },
  PROPERTY_MODULE: {
    PATH: '/properties',
    FULL_PATH: '/properties',
    DISPLAY_NAME: 'Projects',
    NAME: 'projects',
    IS_MENU: false,
    IS_MENU_LOOKUP: true,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/pngs/side-nav/area.png`,
    PERMITTED_ROLES: [
      UserRole.E_COMMERCE,
      UserRole.ECOMMERCE_SPECIALIST,
      UserRole.ECOMMERCE_SENIOR_SPECIALIST,
      UserRole.OPERATION_MANAGER,
      UserRole.HOSPITALITY_MANAGER,
      UserRole.SUPER_ADMIN,
      UserRole.ADMIN
    ],
    CHILDREN: {
      LIST: {
        PATH: '/list',
        FULL_PATH: '/properties/list',
        NAME: 'Projects List',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: [
          UserRole.E_COMMERCE,
          UserRole.ECOMMERCE_SPECIALIST,
          UserRole.ECOMMERCE_SENIOR_SPECIALIST,
          UserRole.OPERATION_MANAGER,
          UserRole.HOSPITALITY_MANAGER,
          UserRole.SUPER_ADMIN,
          UserRole.ADMIN
        ]
      },
      EDIT: {
        PATH: '/edit/:id',
        FULL_PATH: '/properties/edit/:id',
        NAME: 'Edit Project',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: [
          UserRole.E_COMMERCE,
          UserRole.ECOMMERCE_SPECIALIST,
          UserRole.ECOMMERCE_SENIOR_SPECIALIST,
          UserRole.OPERATION_MANAGER,
          UserRole.SUPER_ADMIN,
          UserRole.ADMIN
        ]
      }
    }
  },
  UNIT_TYPE_MODULE: {
    PATH: '/unit-types',
    FULL_PATH: '/unit-types',
    DISPLAY_NAME: 'Unit Types',
    NAME: 'unit-types',
    IS_MENU: false,
    IS_MENU_LOOKUP: true,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/unit-types.svg`,
    PERMITTED_ROLES: [
      UserRole.E_COMMERCE,
      UserRole.ECOMMERCE_SPECIALIST,
      UserRole.ECOMMERCE_SENIOR_SPECIALIST,
      UserRole.OPERATION_MANAGER,
      UserRole.HOSPITALITY_MANAGER,
      UserRole.HOUSE_KEEPING_MANAGER,
      UserRole.SUPER_ADMIN,
      UserRole.ADMIN
    ],
    CHILDREN: {
      LIST: {
        PATH: '/list',
        FULL_PATH: '/unit-types/list',
        NAME: 'projects List',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: [
          UserRole.E_COMMERCE,
          UserRole.ECOMMERCE_SPECIALIST,
          UserRole.ECOMMERCE_SENIOR_SPECIALIST,
          UserRole.OPERATION_MANAGER,
          UserRole.HOSPITALITY_MANAGER,
          UserRole.HOUSE_KEEPING_MANAGER,
          UserRole.SUPER_ADMIN,
          UserRole.ADMIN
        ]
      },
      EDIT: {
        PATH: '/edit/:id',
        FULL_PATH: '/unit-types/edit/:id',
        NAME: 'Edit project',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: [
          UserRole.E_COMMERCE,
          UserRole.ECOMMERCE_SPECIALIST,
          UserRole.ECOMMERCE_SENIOR_SPECIALIST,
          UserRole.HOUSE_KEEPING_MANAGER,
          UserRole.OPERATION_MANAGER,
          UserRole.HOSPITALITY_MANAGER,
          UserRole.SUPER_ADMIN,
          UserRole.ADMIN
        ]
      }
    }
  },
  BULK_ACTIONS_MODULE: {
    PATH: '/bulk-actions',
    FULL_PATH: '/bulk-actions',
    DISPLAY_NAME: 'Bulk Actions',
    NAME: 'bulk-actions',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/bulk-list.svg`,
    PERMITTED_ROLES: [
      UserRole.SUPER_ADMIN,
      UserRole.ADMIN,
      UserRole.ECOMMERCE_SENIOR_SPECIALIST,
      UserRole.ECOMMERCE_SPECIALIST
    ]
  },
  UNIT_LIFE_CYCLE_MODULE: {
    PATH: '/unit-life-cycle',
    FULL_PATH: '/unit-life-cycle',
    DISPLAY_NAME: 'Unit Life Cycle',
    NAME: 'unit-life-cycle',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/unit-types.svg`,
    PERMITTED_ROLES: [
      UserRole.SUPER_ADMIN,
      UserRole.ADMIN,
      UserRole.ECOMMERCE_SENIOR_SPECIALIST,
      UserRole.ECOMMERCE_SPECIALIST
    ]
  },
  EXPENSES_MODULE: {
    PATH: '/expenses',
    FULL_PATH: '/expenses',
    DISPLAY_NAME: 'Expenses',
    NAME: 'expenses',
    IS_MENU: false,
    IS_MENU_LOOKUP: true,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/expenses.svg`,
    PERMITTED_ROLES: [
      UserRole.E_COMMERCE,
      UserRole.FINANCE_MANAGER,
      UserRole.FINANCE_SPECIALIST,
      UserRole.OPERATION_MANAGER,
      UserRole.SUPER_ADMIN,
      UserRole.ADMIN
    ]
  },
  PAYOUT_MODULE: {
    PATH: '/payout',
    FULL_PATH: '/payout',
    DISPLAY_NAME: 'Payout',
    NAME: 'payout',
    IS_MENU: false,
    IS_MENU_LOOKUP: true,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/payout.svg`,
    PERMITTED_ROLES: [
      UserRole.E_COMMERCE,
      UserRole.FINANCE_MANAGER,
      UserRole.ADMIN,
      UserRole.SUPER_ADMIN,
      UserRole.FINANCE_SPECIALIST
    ]
  },
  RESERVATION_MODULE: {
    PATH: '/reservations',
    FULL_PATH: '/reservations',
    DISPLAY_NAME: 'Reservations',
    NAME: 'reservations',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    NOTIFICATIONS_KEY: 'reservations',
    ICON: `${PUBLIC_PATH}/assets/pngs/side-nav/reservation.png`,
    PERMITTED_ROLES: [
      UserRole.RESERVATION_AGENT,
      UserRole.RESERVATION_MANAGER,
      UserRole.ADMIN,
      UserRole.SUPER_ADMIN,
      UserRole.COMMERCIAL_MANAGER,
      UserRole.RESERVATIONS,
      UserRole.HOSPITALITY_AGENT,
      UserRole.HOSPITALITY_LEAD,
      UserRole.GES,
      UserRole.HOSPITALITY_MANAGER,
      UserRole.FINANCE_MANAGER,
      UserRole.FINANCE_SPECIALIST,
      UserRole.OPERATION_MANAGER,
      UserRole.ECOMMERCE_SENIOR_SPECIALIST,
      UserRole.SALES_SPECIALIST,
      UserRole.SALES_MANAGER
    ],
    CHILDREN: {
      LIST: {
        PATH: '/list',
        FULL_PATH: '/reservations/list',
        NAME: 'Reservations List',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: [
          UserRole.RESERVATION_AGENT,
          UserRole.RESERVATION_MANAGER,
          UserRole.ADMIN,
          UserRole.SUPER_ADMIN,
          UserRole.COMMERCIAL_MANAGER,
          UserRole.RESERVATIONS,
          UserRole.SALES_SPECIALIST,
          UserRole.SALES_MANAGER,
          UserRole.HOSPITALITY_AGENT,
          UserRole.HOSPITALITY_LEAD,
          UserRole.GES,
          UserRole.HOSPITALITY_MANAGER,
          UserRole.FINANCE_MANAGER,
          UserRole.FINANCE_SPECIALIST,
          UserRole.OPERATION_MANAGER,
          UserRole.ECOMMERCE_SENIOR_SPECIALIST
        ]
      },
      EDIT: {
        PATH: '/edit/:id',
        FULL_PATH: '/reservations/edit/:id',
        NAME: 'Edit Reservation',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: [
          UserRole.RESERVATION_AGENT,
          UserRole.RESERVATION_MANAGER,
          UserRole.ADMIN,
          UserRole.SUPER_ADMIN,
          UserRole.COMMERCIAL_MANAGER,
          UserRole.RESERVATIONS,
          UserRole.SALES_SPECIALIST,
          UserRole.SALES_MANAGER,
          UserRole.HOSPITALITY_AGENT,
          UserRole.HOSPITALITY_LEAD,
          UserRole.GES,
          UserRole.HOSPITALITY_MANAGER,
          UserRole.FINANCE_MANAGER,
          UserRole.FINANCE_SPECIALIST,
          UserRole.OPERATION_MANAGER,
          UserRole.ECOMMERCE_SENIOR_SPECIALIST
        ]
      },
      EDIT_OLD: {
        PATH: 'edit-old/:id',
        FULL_PATH: '/reservations/edit-old/:id',
        NAME: 'Old Edit Reservation',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: [
          UserRole.RESERVATION_AGENT,
          UserRole.RESERVATION_MANAGER,
          UserRole.ADMIN,
          UserRole.SUPER_ADMIN,
          UserRole.COMMERCIAL_MANAGER,
          UserRole.RESERVATIONS,
          UserRole.SALES_SPECIALIST,
          UserRole.SALES_MANAGER,
          UserRole.HOSPITALITY_AGENT,
          UserRole.GES,
          UserRole.HOSPITALITY_MANAGER,
          UserRole.FINANCE_MANAGER,
          UserRole.FINANCE_SPECIALIST,
          UserRole.OPERATION_MANAGER,
          UserRole.ECOMMERCE_SENIOR_SPECIALIST,
          UserRole.HOSPITALITY_LEAD
        ]
      },
      FEEDBACK_LIST: {
        PATH: '/feedbacks',
        FULL_PATH: '/reservations/feedbacks',
        DISPLAY_NAME: 'Feedbacks',
        NAME: 'feedbacks',
        IS_MENU: false,
        IS_MENU_LOOKUP: true,
        IS_MENU_USERS: false,
        ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/feedback.svg`,
        PERMITTED_ROLES: [
          UserRole.ADMIN,
          UserRole.SUPER_ADMIN,
          UserRole.HOSPITALITY_AGENT,
          UserRole.HOSPITALITY_LEAD,
          UserRole.GES,
          UserRole.HOSPITALITY_MANAGER,
          UserRole.OPERATION_MANAGER,
          UserRole.HOUSE_KEEPING_MANAGER,
          UserRole.RESERVATION_MANAGER
        ]
      },
      FEEDBACK_DETAILS: {
        PATH: '/feedback/:id',
        FULL_PATH: '/reservations/feedback/:id',
        NAME: 'Feedback details',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: [
          UserRole.ADMIN,
          UserRole.SUPER_ADMIN,
          UserRole.HOSPITALITY_AGENT,
          UserRole.HOSPITALITY_LEAD,
          UserRole.GES,
          UserRole.HOSPITALITY_MANAGER,
          UserRole.OPERATION_MANAGER,
          UserRole.HOUSE_KEEPING_MANAGER,
          UserRole.RESERVATION_MANAGER
        ]
      }
    }
  },
  OPERATION_MODULE: {
    PATH: '/operation',
    FULL_PATH: '/operation',
    DISPLAY_NAME: 'Operation',
    NAME: 'Operation',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    IS_COLLAPSIBLE: true,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/amenities.svg`,
    PERMITTED_ROLES: [
      UserRole.SUPER_ADMIN,
      UserRole.ADMIN,
      UserRole.OPERATION_MANAGER,
      UserRole.HOSPITALITY_MANAGER,
      UserRole.GES,
      UserRole.HOSPITALITY_AGENT,
      UserRole.HOSPITALITY_LEAD,
      UserRole.HOUSE_KEEPING_MANAGER,
      UserRole.HOUSE_KEEPING
    ],
    CHILDREN: {
      OPERATION: {
        PATH: '/operation',
        FULL_PATH: '/operation',
        DISPLAY_NAME: 'Operation',
        NAME: 'Operation',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: [
          UserRole.SUPER_ADMIN,
          UserRole.ADMIN,
          UserRole.OPERATION_MANAGER,
          UserRole.HOSPITALITY_MANAGER,
          UserRole.GES,
          UserRole.HOSPITALITY_AGENT,
          UserRole.HOSPITALITY_LEAD,
          UserRole.HOUSE_KEEPING_MANAGER,
          UserRole.HOUSE_KEEPING
        ]
      },
      MAINTENANCE: {
        PATH: '/maintenance',
        FULL_PATH: '/maintenance',
        DISPLAY_NAME: 'Operation Maintenance',
        NAME: 'Operation Maintenance',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: [UserRole.SUPER_ADMIN]
      },
      DAILY_OPERATIONS: {
        PATH: '/daily-operations',
        FULL_PATH: '/daily-operations',
        DISPLAY_NAME: 'Daily Operations',
        NAME: 'Daily Operations',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.OPERATION_MANAGER]
      },
      HOUSE_KEEPING: {
        PATH: '/house-keeping',
        FULL_PATH: '/house-keeping',
        DISPLAY_NAME: 'House Keeping',
        NAME: 'House Keeping',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: [
          UserRole.SUPER_ADMIN,
          UserRole.ADMIN,
          UserRole.OPERATION_MANAGER,
          UserRole.HOUSE_KEEPING,
          UserRole.HOUSE_KEEPING_MANAGER
        ]
      },
      HOUSE_KEEPING_LIST: {
        PATH: '/housekeeping-list',
        FULL_PATH: '/housekeeping-list',
        DISPLAY_NAME: 'House Keeping List',
        NAME: 'House Keeping List',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: [
          UserRole.SUPER_ADMIN,
          UserRole.ADMIN,
          UserRole.OPERATION_MANAGER,
          UserRole.HOUSE_KEEPING_MANAGER
        ]
      },
      GES: {
        PATH: '/ges',
        FULL_PATH: '/ges',
        DISPLAY_NAME: 'GES',
        NAME: 'GES',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: GES_PERMITTED_ROLES
      },
      CX_QUEUE: {
        PATH: '/cx-queue',
        FULL_PATH: '/cx-queue',
        DISPLAY_NAME: 'CX Queue',
        NAME: 'CX Queue',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: CX_QUEUE_PERMITTED_ROLES
      },
      PROPERTY_MANAGERS: {
        PATH: '/property-managers',
        FULL_PATH: '/property-managers',
        DISPLAY_NAME: 'Property Managers',
        NAME: 'Property Managers',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: PROPERTY_MANAGERS_PERMITTED_ROLES
      }
    }
  },
  PROMOCODE_MODULE: {
    PATH: '/promocode',
    FULL_PATH: '/promocode',
    DISPLAY_NAME: 'Promo Code',
    NAME: 'promocode',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/promo-code.svg`,
    PERMITTED_ROLES: PROMOCODES_PERMITTED_ROLES
  },
  BROKER_MODULE: {
    PATH: '/brokers',
    FULL_PATH: '/brokers',
    DISPLAY_NAME: 'Brokers',
    NAME: 'brokers',
    IS_MENU: false,
    IS_MENU_LOOKUP: true,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/amenities.svg`,
    PERMITTED_ROLES: [
      UserRole.ADMIN,
      UserRole.SUPER_ADMIN,
      UserRole.RESERVATIONS,
      UserRole.RESERVATION_MANAGER
    ],
    CHILDREN: {
      LIST: {
        PATH: '/list',
        FULL_PATH: '/brokers/list',
        NAME: 'Brokers List',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: [
          UserRole.ADMIN,
          UserRole.SUPER_ADMIN,
          UserRole.RESERVATIONS,
          UserRole.RESERVATION_MANAGER
        ]
      }
    }
  },
  COMPANY_MODULE: {
    PATH: '/companies',
    FULL_PATH: '/companies',
    DISPLAY_NAME: 'Companies',
    NAME: 'companies',
    IS_MENU: false,
    IS_MENU_LOOKUP: true,
    IS_MENU_USERS: false,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/amenities.svg`,
    PERMITTED_ROLES: [UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.RESERVATIONS],
    CHILDREN: {
      LIST: {
        PATH: '/list',
        FULL_PATH: '/companies/list',
        NAME: 'Companies List',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: [UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.RESERVATIONS]
      }
    }
  },
  PAYMENTS_MODULE: {
    PATH: '/payments',
    FULL_PATH: '/payments',
    DISPLAY_NAME: 'Payments',
    NAME: 'payments',
    IS_MENU: false,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: true,
    // Collapsible since the module gained a second page. Clicking 'Payments' now expands the
    // submenu instead of navigating straight to the list; /payments still redirects to the list,
    // so any existing bookmark keeps working.
    IS_COLLAPSIBLE: true,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/payments.svg`,
    PERMITTED_ROLES: PAYMENTS_PERMITTED_ROLES,
    CHILDREN: {
      LIST: {
        PATH: '/list',
        FULL_PATH: '/payments/list',
        DISPLAY_NAME: 'Payments List',
        NAME: 'Payments List',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: PAYMENTS_PERMITTED_ROLES
      },
      OVERDUE: {
        PATH: '/overdue',
        FULL_PATH: '/payments/overdue',
        DISPLAY_NAME: 'Overdue Payments',
        NAME: 'Overdue Payments',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: PAYMENTS_PERMITTED_ROLES
      }
    }
  },
  FINANCE_MODULE: {
    PATH: '/finance',
    FULL_PATH: '/finance',
    DISPLAY_NAME: 'Finance',
    NAME: 'Finance',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    IS_COLLAPSIBLE: true,
    NEW_LABEL: true,
    PERMITTED_ROLES: FINANCE_PERMITTED_ROLES,
    CHILDREN: {
      LANDLORD_DETAILED_REPORT: {
        PATH: '/landlord-detailed-report',
        FULL_PATH: '/finance/landlord-detailed-report',
        DISPLAY_NAME: 'Landlord Detailed Report',
        NAME: 'Landlord Detailed Report',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: FINANCE_PERMITTED_ROLES
      }
    }
  },
  MARKETING_MODULE: {
    PATH: '/marketing',
    FULL_PATH: '/marketing',
    DISPLAY_NAME: 'Marketing',
    NAME: 'Marketing',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    IS_COLLAPSIBLE: true,
    NEW_LABEL: true,
    PERMITTED_ROLES: MARKETING_PERMITTED_ROLES,
    CHILDREN: {
      GUEST_AUDIENCE: {
        PATH: '/guest-audience',
        FULL_PATH: '/marketing/guest-audience',
        DISPLAY_NAME: 'Guest Audience',
        NAME: 'Guest Audience',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: MARKETING_GUEST_AUDIENCE_PERMITTED_ROLES
      },
      BLOGS: {
        PATH: '/blogs',
        FULL_PATH: '/marketing/blogs',
        DISPLAY_NAME: 'Blogs',
        NAME: 'blogs',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/blogs.svg`,
        PERMITTED_ROLES: MARKETING_BLOGS_PERMITTED_ROLES,
        CHILDREN: {
          LIST: {
            PATH: '/list',
            FULL_PATH: '/marketing/blogs/list',
            NAME: 'Blogs List',
            IS_MENU: false,
            IS_MENU_LOOKUP: false,
            IS_MENU_USERS: false,
            PERMITTED_ROLES: MARKETING_BLOGS_PERMITTED_ROLES
          },
          NEW: {
            PATH: '/new',
            FULL_PATH: '/marketing/blogs/new',
            NAME: 'New Blog',
            IS_MENU: false,
            IS_MENU_LOOKUP: false,
            IS_MENU_USERS: false,
            PERMITTED_ROLES: MARKETING_BLOGS_PERMITTED_ROLES
          },
          EDIT: {
            PATH: '/edit/:id',
            FULL_PATH: '/marketing/blogs/edit/:id',
            NAME: 'Edit Blog',
            IS_MENU: false,
            IS_MENU_LOOKUP: false,
            IS_MENU_USERS: false,
            PERMITTED_ROLES: MARKETING_BLOGS_PERMITTED_ROLES
          }
        }
      }
    }
  },
  // REPORTS_MODULE: {
  //   PATH: '/reports',
  //   FULL_PATH: '/reports',
  //   DISPLAY_NAME: 'Reports',
  //   NAME: 'Reports',
  //   IS_MENU: false,
  //   IS_MENU_LOOKUP: false,
  //   IS_MENU_USERS: false,
  //   ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/reports.svg`,
  //   PERMITTED_ROLES: [
  //     UserRole.FINANCE_MANAGER,
  //     UserRole.ADMIN,
  //     UserRole.SUPER_ADMIN,
  //     UserRole.FINANCE_SPECIALIST
  //   ],
  //   CHILDREN: {
  //     FINANCIAL: {
  //       PATH: '/financial',
  //       FULL_PATH: '/reports/financial',
  //       NAME: 'Financial Report',
  //       ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/financial-report.svg`,
  //       DISPLAY_NAME: 'Financial Report',
  //       IS_MENU: false,
  //       IS_MENU_LOOKUP: true,
  //       IS_MENU_USERS: false,
  //       PERMITTED_ROLES: [
  //         UserRole.FINANCE_MANAGER,
  //         UserRole.ADMIN,
  //         UserRole.SUPER_ADMIN,
  //         UserRole.FINANCE_SPECIALIST
  //       ]
  //     }
  //   }
  // },
  FURNISHING_MANAGEMENT_MODULE: {
    PATH: '/furnishing-main',
    FULL_PATH: '/furnishing-main',
    DISPLAY_NAME: 'Furnishing',
    NAME: 'furnishing-management',
    IS_MENU: false,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: true,
    IS_COLLAPSIBLE: true,
    NEW_LABEL: true,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/user-profile.svg`,
    PERMITTED_ROLES: [
      UserRole.SUPER_ADMIN,
      UserRole.ADMIN,
      UserRole.DESIGN_TEAM,
      UserRole.DESIGN_MANAGER,
      UserRole.SALES_SPECIALIST,
      UserRole.SALES_MANAGER,
      UserRole.SALES_MANAGER
    ],
    CHILDREN: {
      PACKAGES: {
        PATH: '/packages',
        FULL_PATH: '/packages',
        DISPLAY_NAME: 'Packages',
        NAME: 'Furnishing Packages',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: true,
        IS_SUBMENU: true,
        PERMITTED_ROLES: [
          UserRole.SUPER_ADMIN,
          UserRole.ADMIN,
          UserRole.DESIGN_MANAGER,
          UserRole.DESIGN_TEAM
        ]
      },
      REQUESTS: {
        PATH: '/requests',
        FULL_PATH: '/requests',
        DISPLAY_NAME: 'Requests',
        NAME: 'Furnishing Requests',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: true,
        IS_SUBMENU: true,
        PERMITTED_ROLES: [
          UserRole.SUPER_ADMIN,
          UserRole.ADMIN,
          UserRole.DESIGN_TEAM,
          UserRole.DESIGN_MANAGER,
          UserRole.SALES_SPECIALIST,
          UserRole.SALES_MANAGER,
          UserRole.SALES_MANAGER
        ]
      }
    }
  },
  LEAD_MODULE: {
    PATH: '/leads',
    FULL_PATH: '/leads',
    DISPLAY_NAME: 'Leads',
    NAME: 'leads',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    IS_COLLAPSIBLE: true,
    NEW_LABEL: true,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/user-profile.svg`,
    PERMITTED_ROLES: [
      UserRole.SUPER_ADMIN,
      UserRole.ADMIN,
      UserRole.SALES_MANAGER,
      UserRole.SALES_SPECIALIST,
      UserRole.SALES_PERSON,
      UserRole.COMMERCIAL_MANAGER,
      UserRole.MARKETING_SPECIALIST,
      UserRole.MARKETING_CAMPAIGNS,
      UserRole.RESERVATIONS,
      UserRole.RESERVATION_MANAGER
    ],
    CHILDREN: {
      LIST: {
        PATH: '/list',
        FULL_PATH: '/leads/list',
        DISPLAY_NAME: 'All Leads',
        NAME: 'Leads List',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: [
          UserRole.SUPER_ADMIN,
          UserRole.ADMIN,
          UserRole.SALES_MANAGER,
          UserRole.SALES_SPECIALIST,
          UserRole.SALES_PERSON,
          UserRole.COMMERCIAL_MANAGER,
          UserRole.MARKETING_SPECIALIST,
          UserRole.MARKETING_CAMPAIGNS,
          UserRole.RESERVATIONS,
          UserRole.RESERVATION_MANAGER
        ]
      },
      OPS: {
        PATH: '/ops',
        FULL_PATH: '/leads/ops',
        DISPLAY_NAME: 'Leads Ops',
        NAME: 'Leads Ops',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: [
          UserRole.SUPER_ADMIN,
          UserRole.ADMIN,
          UserRole.SALES_MANAGER,
          UserRole.SALES_SPECIALIST,
          UserRole.SALES_PERSON,
          UserRole.COMMERCIAL_MANAGER,
          UserRole.MARKETING_SPECIALIST,
          UserRole.MARKETING_CAMPAIGNS,
          UserRole.RESERVATIONS,
          UserRole.RESERVATION_MANAGER
        ]
      },
      DETAILS: {
        PATH: '/details/:leadId',
        FULL_PATH: '/leads/details/:leadId',
        DISPLAY_NAME: 'Lead',
        NAME: 'Lead Details',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: [
          UserRole.SUPER_ADMIN,
          UserRole.ADMIN,
          UserRole.SALES_MANAGER,
          UserRole.SALES_SPECIALIST,
          UserRole.SALES_PERSON,
          UserRole.COMMERCIAL_MANAGER,
          UserRole.MARKETING_SPECIALIST,
          UserRole.MARKETING_CAMPAIGNS,
          UserRole.RESERVATIONS,
          UserRole.RESERVATION_MANAGER
        ]
      }
    }
  },
  TICKETS_MODULE: {
    PATH: '/tickets',
    FULL_PATH: '/tickets',
    DISPLAY_NAME: 'Tickets',
    NAME: 'tickets',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    IS_COLLAPSIBLE: true,
    NEW_LABEL: true,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/user-profile.svg`,
    PERMITTED_ROLES: TICKETS_PERMITTED_ROLES,
    CHILDREN: {
      QUEUE: {
        PATH: '/queue',
        FULL_PATH: '/tickets/queue',
        DISPLAY_NAME: 'Tickets',
        NAME: 'Tickets Queue',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: TICKETS_PERMITTED_ROLES
      },
      DETAILS: {
        PATH: '/details/:ticketId',
        FULL_PATH: '/tickets/details/:ticketId',
        DISPLAY_NAME: 'Ticket',
        NAME: 'Ticket Details',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        PERMITTED_ROLES: TICKETS_PERMITTED_ROLES
      }
    }
  },
  TEAM_MODULE: {
    PATH: '/teams',
    FULL_PATH: '/teams',
    DISPLAY_NAME: 'Teams',
    NAME: 'teams',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    IS_COLLAPSIBLE: true,
    NEW_LABEL: true,
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/user-profile.svg`,
    PERMITTED_ROLES: [
      UserRole.SUPER_ADMIN,
      UserRole.ADMIN,
      UserRole.SALES_MANAGER,
      UserRole.COMMERCIAL_MANAGER
    ],
    CHILDREN: {
      STRUCTURE: {
        PATH: '/structure',
        FULL_PATH: '/teams/structure',
        DISPLAY_NAME: 'Structure',
        NAME: 'Team Structure',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: [
          UserRole.SUPER_ADMIN,
          UserRole.ADMIN,
          UserRole.SALES_MANAGER,
          UserRole.COMMERCIAL_MANAGER
        ]
      },
      LIST: {
        PATH: '/list',
        FULL_PATH: '/teams/list',
        DISPLAY_NAME: 'Teams & Access',
        NAME: 'Teams List',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: [
          UserRole.SUPER_ADMIN,
          UserRole.ADMIN,
          UserRole.SALES_MANAGER,
          UserRole.COMMERCIAL_MANAGER
        ]
      },
      ASSIGNMENT: {
        PATH: '/assignment',
        FULL_PATH: '/teams/assignment',
        DISPLAY_NAME: 'Assignment',
        NAME: 'Assignment Settings',
        IS_MENU: false,
        IS_MENU_LOOKUP: false,
        IS_MENU_USERS: false,
        IS_SUBMENU: true,
        PERMITTED_ROLES: [
          UserRole.SUPER_ADMIN,
          UserRole.ADMIN,
          UserRole.SALES_MANAGER,
          UserRole.COMMERCIAL_MANAGER
        ]
      }
    }
  }
};

export const BLOG_ROUTES = (APP_ROUTES.MARKETING_MODULE.CHILDREN as RouteConfig).BLOGS;

export const SETTINGS_ROUTES: RouteConfig = {
  // ACCOUNT_SETTINGS_MODULE: {
  //   PATH: '/settings',
  //   FULL_PATH: '/setting',
  //   DISPLAY_NAME: 'Account Settings',
  //   IS_MENU: true,
  //   IS_MENU_LOOKUP: false,
  //   IS_MENU_USERS: false,
  //   NAME: 'account settings',
  //   ICON: `${PUBLIC_PATH}/assets/pngs/side-nav/settings.png`
  // },
  ACCOUNT_CONFIGURATION_MODULE: {
    PATH: '/configuration',
    FULL_PATH: '/configuration',
    DISPLAY_NAME: 'Configuration',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    NAME: 'configuration',
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/settings.svg`,
    PERMITTED_ROLES: [UserRole.ADMIN, UserRole.SUPER_ADMIN]
  },
  USER_PROFILE_MODULE: {
    PATH: '/user-profile',
    FULL_PATH: '/user-profile',
    DISPLAY_NAME: 'Profile',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    NAME: 'user profile',
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/user-profile.svg`
  },
  LOGOUT_MODULE: {
    PATH: '/logout',
    FULL_PATH: '/logout',
    DISPLAY_NAME: 'Logout',
    IS_MENU: true,
    IS_MENU_LOOKUP: false,
    IS_MENU_USERS: false,
    NAME: 'logout',
    ICON: `${PUBLIC_PATH}/assets/svgs/side-nav/logout.svg`
  }
};
